import * as React from 'react';
import styles from './index.less';
import { useParams } from 'react-router-dom';
import { connect } from 'dva';
import {
  ConnectState,
  CollectionManagerState,
  CollectionCreatorPageModelState,
} from '@/models/connect';
import { Dispatch } from 'redux';
import * as AHooks from 'ahooks';
import useUrlState from '@/hooks/useUrlState';
import FComponentsLib from '@freelog/components-lib';
import { Space, Pagination, Modal } from 'antd';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import {
  ChangeAction,
  Version_OnMount_Page_Action,
  Version_OnUnmount_Page_Action,
  Sider_OnChange_Page_Action,
  Sider_OnMount_Page_Action,
  Version_Fetch_CollectionItems_Action,
  Version_FetchDataSourceAction,
  Version_SaveDraft_Action,
  Version_SaveDate_Action,
  Version_SetRssSource_Action,
} from '@/models/collectionManager';
import fResourceOptionEditor from '@/components/fResourceOptionEditor';
import FResourceOptions from '@/components/FResourceOptions';
import FBraftEditor from '@/components/FBraftEditor';
import BraftEditor, { EditorState } from 'braft-editor';
import FViewportCards_Resource from '@/components/FAntvG6/FViewportCards_Resource';
import FMicroAPP_Authorization from '@/components/FMicroAPP_Authorization';
import FSkeletonNode from '@/components/FSkeletonNode';
import FHtmlReactParser from '@/components/FHtmlReactParser';
import CollectionSetting from '@/components/FCollectionSetting';
import FTooltip from '@/components/FTooltip';
import FLoadingTip from '@/components/FLoadingTip';
import fMessage from '@/components/fMessage';
import ChangeLogDrawer from '@/pages/resource/collectionSidebar/versionInfo/$id/ChangeLogDrawer';
import fSelectResourcesAsCollectionItems from '@/components/fSelectResourcesAsCollectionItems';
import FContractHandleDrawer from '@/components/FContractHandleDrawer';
import FAddResourcesHandleAuth from '@/components/FAddResourcesHandleAuth';
import FIcons from '@/components/FIcons';
import FCollectionItems2 from '@/components/FCollectionItems2';
import SortOrderModal from '@/components/FSortOrderModal';
import FAttrsAndConfigs, { IAttrsAndConfigsItem } from '@/components/FAttrsAndConfigs';
import fResourcePropertyEditor3 from '@/components/fResourcePropertyEditor3';
import FInput_Search from '@/components/FInput_Search';
import FPodcastRssSubmit, {
  checkPodcastRssUrl,
  FPodcastRssSubmitMethodPicker,
  FPodcastRssImportingPanel,
  FRssFeedIcon,
  throwBindRssFeedError,
} from '@/components/FPodcastRssSubmit';
import { isRssSyncImporting } from '@/utils/rss';

interface VersionInfoProps {
  dispatch: Dispatch;
  collectionManager: CollectionManagerState;
}

function VersionInfo({ dispatch, collectionManager }: VersionInfoProps) {
  const params = useParams<{ id: string }>();
  const ref_ItemsTitle = React.useRef<any>(null);
  const [$urlState, set$urlState] = useUrlState<{ version: string }>({
    version: '',
  });
  const [
    $collectionSettingExpansion,
    set$collectionSettingExpansion,
    get$collectionSettingExpansion,
  ] = FUtil.Hook.useGetState<'style' | 'sort' | ''>('');
  const [$logDrawerOpen, set$logDrawerOpen, get$logDrawerOpen] =
    FUtil.Hook.useGetState<boolean>(false);

  const [$authProcessorShow, set$authProcessorShow, get$authProcessorShow] =
    FUtil.Hook.useGetState<number>(0);
  const [$licensorId, set$licensorId, get$licensorId] = FUtil.Hook.useGetState<string>('');
  const [$itemId, set$itemId, get$itemId] = FUtil.Hook.useGetState<string>('');

  const [$resourceIDs, set$resourceIDs, get$resourceIDs] = FUtil.Hook.useGetState<string[]>([]);

  const [$itemSubmitMode, set$itemSubmitMode] = FUtil.Hook.useGetState<'' | 'rss'>('');

  const [$sortOrderModalShow, set$sortOrderModalShow, get$sortOrderModalShow] =
    FUtil.Hook.useGetState<boolean>(false);

  const [$syncing, set$syncing, get$syncing] = FUtil.Hook.useGetState<boolean>(false);
  // syncBinding 已发出、progress 尚未回到 importing 时的短暂窗口，防止连点
  const [$awaitingSyncProgress, set$awaitingSyncProgress, get$awaitingSyncProgress] =
    FUtil.Hook.useGetState<boolean>(false);

  async function onClickSync() {
    // 进行中（pending/running）或请求未完成时，不再发送同步请求
    const progressStatus = collectionManager.rss_syncProgress?.status;
    if (
      get$syncing() ||
      get$awaitingSyncProgress() ||
      (collectionManager.rss_syncProgress != null && isRssSyncImporting(progressStatus))
    ) {
      return;
    }
    const resourceId = collectionManager.version_resourceID;
    if (!resourceId) {
      return;
    }
    set$syncing(true);
    try {
      const { ret, errCode, msg } = await FServiceAPI.Rss.syncBinding({
        resourceId,
      });
      if (ret !== 0 || errCode !== 0) {
        fMessage(msg, 'error');
        return;
      }
      set$awaitingSyncProgress(true);
      // 通知侧栏立即拉取同步进度，展示导入横幅
      window.dispatchEvent(new CustomEvent('freelog:rss-sync-requested'));
    } finally {
      set$syncing(false);
    }
  }

  AHooks.useMount(async () => {
    dispatch<Sider_OnMount_Page_Action>({
      type: 'collectionManager/sider_onMount_Page',
      payload: {
        resourceID: params.id!,
      },
    });

    dispatch<Sider_OnChange_Page_Action>({
      type: 'collectionManager/sider_onChange_Page',
      payload: {
        page: 'versionInfo',
      },
    });
    dispatch<Version_OnMount_Page_Action>({
      type: 'collectionManager/version_onMount_Page',
      payload: {
        resourceID: params.id!,
        version: $urlState.version,
      },
    });
  });

  AHooks.useUnmount(() => {
    dispatch<Version_OnUnmount_Page_Action>({
      type: 'collectionManager/version_onUnmount_Page',
    });
  });

  const { run: keywordsRun } = AHooks.useDebounceFn(
    () => {
      dispatch<Version_Fetch_CollectionItems_Action>({
        type: 'collectionManager/version_Fetch_CollectionItems',
      });
    },
    {
      wait: 300,
    },
  );

  const { run: run_saveDraft } = AHooks.useDebounceFn(
    () => {
      dispatch<Version_SaveDraft_Action>({
        type: 'collectionManager/version_SaveDraft',
      });
    },
    {
      wait: 300,
    },
  );

  async function onChange_needSaveDraft(payload: Partial<CollectionManagerState>) {
    await dispatch<ChangeAction>({
      type: 'collectionManager/change',
      payload: {
        ...payload,
        version_otherChanged: true,
      },
    });
    run_saveDraft();
  }

  async function onChange_needSaveDraft_collectionItems(payload: Partial<CollectionManagerState>) {
    await dispatch<ChangeAction>({
      type: 'collectionManager/change',
      payload: {
        ...payload,
        version_collectionItemsChanged: true,
      },
    });
    run_saveDraft();
  }

  async function onChangePage(page: number, pageSize: number) {
    const version_resources_pageCurrent = collectionManager.version_resources_pageCurrent;
    await dispatch<ChangeAction>({
      type: 'collectionManager/change',
      payload: {
        version_resources_pageCurrent: page,
        version_resources_pageSize: pageSize,
      },
    });
    await dispatch<Version_Fetch_CollectionItems_Action>({
      type: 'collectionManager/version_Fetch_CollectionItems',
    });
    if (page !== version_resources_pageCurrent) {
      ref_ItemsTitle.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  const rssSource = collectionManager.version_resourceInfo?.rssSource ?? 'unknown';
  const showEntryFlow = rssSource === 'unknown';
  const isRssCollection = rssSource === 'yes';
  const canShowLibrary =
    collectionManager.version_resourceTypeConfig.uploadEntry.includes('collectionLibrary');
  const canShowRss =
    collectionManager.version_resourceTypeConfig.uploadEntry.includes('podcastRss');
  // 仅库添加 / 未配置 RSS：跳过选择卡，直接进单品管理（对齐创建合集 library 默认态）
  const libraryOnlyEntry = canShowLibrary && !canShowRss;
  const rssOnlyEntry = !canShowLibrary && canShowRss;
  const showMainManagement =
    rssSource === 'yes' ||
    rssSource === 'no' ||
    (showEntryFlow && libraryOnlyEntry) ||
    (showEntryFlow && !canShowLibrary && !canShowRss);
  const syncProgress = collectionManager.rss_syncProgress;
  const isSyncImporting =
    syncProgress != null && isRssSyncImporting(syncProgress.status);
  // 同步未完成前禁用「同步更新」，避免连续点击触发后端错误
  const syncButtonDisabled = $syncing || $awaitingSyncProgress || isSyncImporting;
  const showFullImportPanel =
    isRssCollection &&
    !!syncProgress &&
    isSyncImporting &&
    syncProgress.fullImportCompleted === false;
  // 普通合集且单品为 0、同时存在两种添加方式时，可重新选择
  const showReselectMethod =
    !isRssCollection &&
    collectionManager.version_resources_state === 'noData' &&
    canShowLibrary &&
    canShowRss;

  // progress 已反映导入中/已结束时，解除本地 awaiting 锁
  React.useEffect(() => {
    if (!$awaitingSyncProgress) {
      return;
    }
    if (syncProgress == null) {
      return;
    }
    set$awaitingSyncProgress(false);
  }, [syncProgress, $awaitingSyncProgress]);

  async function setCollectionRssSource(rssSourceValue: 'yes' | 'no' | 'unknown') {
    await dispatch<Version_SetRssSource_Action>({
      type: 'collectionManager/version_setRssSource',
      payload: {
        rssSource: rssSourceValue,
      },
    });
  }

  // 仅一种添加方式时跳过选择页，并落盘 rssSource（对齐创建合集 Step2）
  React.useEffect(() => {
    if (!showEntryFlow || $itemSubmitMode !== '') {
      return;
    }
    if (libraryOnlyEntry || (!canShowLibrary && !canShowRss)) {
      setCollectionRssSource('no');
    } else if (rssOnlyEntry) {
      set$itemSubmitMode('rss');
    }
  }, [showEntryFlow, $itemSubmitMode, libraryOnlyEntry, rssOnlyEntry, canShowLibrary, canShowRss]);

  // 仅当库添加与 RSS 两种入口都存在时才展示选择卡片
  const showMethodPicker =
    showEntryFlow && $itemSubmitMode !== 'rss' && canShowLibrary && canShowRss;

  return (
    <>
      {collectionManager.version_pageState === 'loading' && (
        <div className={styles.skeleton}>
          <div style={{ height: 40 }} />
          <FSkeletonNode width={120} height={22} />
          <div style={{ height: 20 }} />
          <FSkeletonNode width={340} height={38} />
          <div style={{ height: 50 }} />
          <FSkeletonNode width={120} height={22} />
          <div style={{ height: 20 }} />
          <FSkeletonNode width={340} height={38} />
          <div style={{ height: 50 }} />
          <FSkeletonNode width={120} height={22} />
          <div style={{ height: 20 }} />
          <FSkeletonNode width={860} height={38} />
          <div style={{ height: 50 }} />
          <FSkeletonNode width={120} height={22} />
          <div style={{ height: 20 }} />
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20 }}>
            <FSkeletonNode width={200} height={150} />
            <FSkeletonNode width={460} height={40} />
          </div>
          <div style={{ height: 50 }} />
          <FSkeletonNode width={120} height={22} />
          <div style={{ height: 20 }} />
          <FSkeletonNode width={340} height={38} />
        </div>
      )}

      <div
        style={{
          width: '100%',
          display: collectionManager.version_pageState === 'loaded' ? 'block' : 'none',
        }}
      >
        {showEntryFlow && ($itemSubmitMode === 'rss' || showMethodPicker) && (
          <>
            <div style={{ height: 40 }} />
            <div className={styles.contentWidth}>
              {$itemSubmitMode === 'rss' ? (
                <FPodcastRssSubmit
                  resourceId={collectionManager.version_resourceID}
                  onCancel={() => {
                    set$itemSubmitMode('');
                  }}
                  onCheckRssUrl={checkPodcastRssUrl}
                  onSendVerificationCode={async (rssUrl) => {
                    const resourceId = collectionManager.version_resourceID;
                    if (!resourceId) {
                      fMessage('合集资源未创建，请刷新后重试', 'error');
                      return false;
                    }
                    const { ret, errCode, msg, data } =
                      await FServiceAPI.Rss.sendVerificationCode({
                        feedUrl: rssUrl,
                        resourceId,
                      });
                    if (ret !== 0 || errCode !== 0 || !data?.sent) {
                      fMessage(msg || '验证码发送失败', 'error');
                      return false;
                    }
                    return true;
                  }}
                  onStartImport={async (payload) => {
                    const { ret, errCode, msg, data } = await FServiceAPI.Resource.bindRssFeed({
                      resourceId: collectionManager.version_resourceID,
                      feedUrl: payload.rssUrl,
                      verificationCode: payload.verificationCode,
                      pubStartDate: payload.pubStartDate,
                      pubEndDate: payload.pubEndDate,
                    });
                    throwBindRssFeedError({ ret, errCode, msg, data });
                  }}
                  onVerified={async () => {
                    set$itemSubmitMode('');
                    await setCollectionRssSource('yes');
                    window.dispatchEvent(new CustomEvent('freelog:rss-sync-requested'));
                    await dispatch<Version_FetchDataSourceAction>({
                      type: 'collectionManager/version_fetchDataSource',
                    });
                    await dispatch({
                      type: 'collectionManager/sider_onUpdate_Data',
                    });
                  }}
                />
              ) : (
                <FPodcastRssSubmitMethodPicker
                  showLibrary={canShowLibrary}
                  showRss={canShowRss}
                  onSelectLibrary={async () => {
                    set$itemSubmitMode('');
                    await setCollectionRssSource('no');
                  }}
                  onSelectRss={() => {
                    set$itemSubmitMode('rss');
                  }}
                />
              )}
            </div>
            <div style={{ height: 100 }} />
          </>
        )}

        <div
          style={{
            display: showMainManagement ? 'block' : 'none',
          }}
        >
        <div style={{ height: 40 }} />

        <div>
          <div className={styles.pageHeader}>
            <div className={styles.pageHeaderTop}>
              <div className={styles.pageHeaderMain}>
                <FComponentsLib.FTitleText
                  type={'h3'}
                  text={FI18n.i18nNext.t('edit_collection_itemmgnt_itemcount', {
                    itemCount: collectionManager.version_resourceInfo?.itemsTotalCount || 0,
                  })}
                />
                {collectionManager.version_resourceInfo?.isFinish ? (
                  <span className={[styles.statusTag, styles.statusTagCompleted].join(' ')}>
                    {FI18n.i18nNext.t('cqr_itemmgmt_updatedstates_completed')}
                  </span>
                ) : (
                  <span className={[styles.statusTag, styles.statusTagOngoing].join(' ')}>
                    {FI18n.i18nNext.t('cqr_itemmgmt_updatestates_ongoing')}
                  </span>
                )}
              </div>
              {showReselectMethod && (
                <FComponentsLib.FTextBtn
                  type={'primary'}
                  style={{ fontSize: 12 }}
                  onClick={() => {
                    set$itemSubmitMode('');
                    setCollectionRssSource('unknown');
                  }}
                >
                  重新选择添加方式
                </FComponentsLib.FTextBtn>
              )}
            </div>

            <div className={styles.pageHeaderMeta}>
              {isRssCollection ? (
                <div className={styles.pageHeaderMetaRssGroup}>
                  <FRssFeedIcon size={12} color='#999999' />
                  <FComponentsLib.FContentText
                    type={'additional2'}
                    text={FI18n.i18nNext.t('edit_collection_itemmgnt_lastupdated', {
                      Timestamp: collectionManager.version_resourceInfo?.updateDate || '',
                    })}
                  />
                </div>
              ) : (
                <>
                  {collectionManager.version_resourceInfo?.isAutomatic && (
                    <FComponentsLib.FContentText
                      type={'additional2'}
                      text={FI18n.i18nNext.t('edit_collection_label_autoadd_defined')}
                    />
                  )}
                  <FComponentsLib.FContentText
                    type={'additional2'}
                    text={FI18n.i18nNext.t('edit_collection_itemmgnt_lastupdated', {
                      Timestamp: collectionManager.version_resourceInfo?.updateDate || '',
                    })}
                  />
                </>
              )}
              {isRssCollection && (
                <FComponentsLib.FTextBtn
                  type={'primary'}
                  style={{ fontSize: 12 }}
                  disabled={syncButtonDisabled}
                  onClick={onClickSync}
                >
                  同步更新
                </FComponentsLib.FTextBtn>
              )}
              <FComponentsLib.FTextBtn
                type={'primary'}
                style={{ fontSize: 12 }}
                onClick={() => {
                  set$logDrawerOpen(true);
                }}
              >
                {FI18n.i18nNext.tAuto('edit_collection_itemmgnt_btn_viewchangelogs')}
              </FComponentsLib.FTextBtn>
            </div>
          </div>
          <div style={{ height: 40 }} />

          {showFullImportPanel ? (
            <div className={styles.block}>
              <FPodcastRssImportingPanel />
            </div>
          ) : (
          <div className={styles.block}>
            <div ref={ref_ItemsTitle} />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <FComponentsLib.FContentText
                text={
                  // 单品资源管理
                  FI18n.i18nNext.t('cqr_itemmgmt')
                }
                type={'highlight'}
              />
              {collectionManager.version_collectionItems.length > 0 && (
                <FComponentsLib.FTextBtn
                  style={{
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                  type='primary'
                  disabled={isRssCollection}
                  onClick={async () => {
                    const result: string[] | null = await fSelectResourcesAsCollectionItems({
                      collectionID: collectionManager.version_resourceID,
                    });
                    if (!result || result.length === 0) {
                      return;
                    }
                    set$resourceIDs(result);
                  }}
                >
                  <FIcons.FAdd style={{ fontSize: 14 }} />
                  <span>
                    {
                      // 添加单品资源
                      FI18n.i18nNext.tAuto('cqr_itemmgmt_btn_additem')
                    }
                  </span>
                </FComponentsLib.FTextBtn>
              )}
            </div>

            {collectionManager.version_resources_state !== 'noData' && (
              <>
                <div style={{ height: 20 }} />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <FComponentsLib.FContentText
                      // text={`单品数量 ${collectionManager.version_resources_totalCount}`}
                      text={FI18n.i18nNext.t('cqr_itemmgmt_lable_itemqty', {
                        itemCount: collectionManager.version_resources_totalCount,
                      })}
                      type={'additional2'}
                      style={{ color: '#222' }}
                    />
                    <div style={{ position: 'relative' }}>
                      <FComponentsLib.FTextBtn
                        type={'primary'}
                        style={{ fontSize: 12 }}
                        disabled={isRssCollection}
                        onClick={() => {
                          set$collectionSettingExpansion(
                            get$collectionSettingExpansion() === 'style' ? '' : 'style',
                          );
                        }}
                      >
                        {/* 展示样式设置 */}
                        {FI18n.i18nNext.tAuto('cqr_itemmgmt_viewoptions')}
                      </FComponentsLib.FTextBtn>
                      {$collectionSettingExpansion === 'style' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 35,
                            left: '50%',
                            marginLeft: -15,
                            width: 0,
                            height: 0,
                            borderLeft: '15px solid transparent',
                            borderRight: '15px solid transparent',
                            // borderBottom: '15px solid rgba(0, 0, 0, .03)',
                            borderBottom: '15px solid #F7F7F7',
                          }}
                        />
                      )}
                    </div>
                    <FComponentsLib.FTextBtn
                      type={'primary'}
                      style={{ fontSize: 12 }}
                      disabled={isRssCollection}
                      onClick={() => {
                        set$sortOrderModalShow(true);
                      }}
                    >
                      {/* 调整排序 */}
                      {FI18n.i18nNext.tAuto('cqr_itemmgmt_sort')}
                    </FComponentsLib.FTextBtn>
                  </div>

                  {/* <FComponentsLib.FInput.FSearch */}
                  <FInput_Search
                    value={collectionManager.version_resourceKeywords}
                    onChange={(e) => {
                      dispatch<ChangeAction>({
                        type: 'collectionManager/change',
                        payload: {
                          version_resourceKeywords: e.target.value,
                        },
                      });

                      keywordsRun();
                    }}
                    placeholder={FI18n.i18nNext.t('cqr_itemmgmt_search_hint')}
                  />
                </div>
                <div style={{ height: 10 }} />
                {$collectionSettingExpansion === 'style' && !isRssCollection && (
                  <>
                    <div style={{ height: 10 }} />
                    <CollectionSetting
                      onClose={() => {
                        set$collectionSettingExpansion('');
                      }}
                      setting={collectionManager.version_collectionItemsSetting}
                      onChange={async (value) => {
                        await onChange_needSaveDraft({
                          version_collectionItemsSetting: value,
                        });
                      }}
                      onChangeSort={async (sort) => {
                        await dispatch<Version_Fetch_CollectionItems_Action>({
                          type: 'collectionManager/version_Fetch_CollectionItems',
                        });
                      }}
                      onChangeView={async (view) => {
                        // console.log('onChangeView e3w8iujoof;lskdjlkdjk', view);
                        // debugger;
                        await dispatch<ChangeAction>({
                          type: 'collectionManager/change',
                          payload: {
                            version_resources_pageCurrent: 1,
                            version_resources_pageSize: view === 'collection_view_card' ? 6 : 10,
                          },
                        });
                        await dispatch<Version_Fetch_CollectionItems_Action>({
                          type: 'collectionManager/version_Fetch_CollectionItems',
                        });
                      }}
                    />
                  </>
                )}

                <div style={{ height: 10 }} />
                {collectionManager.version_resources_state === 'noSearchResult' && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <FComponentsLib.FContentText text={'暂无搜索结果'} type={'additional2'} />
                  </div>
                )}

                {collectionManager.version_resources_state === 'loading' && (
                  <FLoadingTip height={200} />
                )}

                {collectionManager.version_resources_state === 'loaded' && (
                  <FCollectionItems2
                    draggable={!isRssCollection && $collectionSettingExpansion === 'sort'}
                    showView={
                      collectionManager.version_collectionItemsSetting.collection_view ===
                      'collection_view_list'
                        ? 'list'
                        : 'card'
                    }
                    showCheckbox={$collectionSettingExpansion === 'sort' ? 'show' : 'hide'}
                    showCover={
                      $collectionSettingExpansion === 'sort'
                        ? 'show'
                        : collectionManager.version_collectionItemsSetting
                            .collection_item_image_display === 'collection_item_image_display_show'
                        ? 'show'
                        : 'hide'
                    }
                    showNumber={
                      $collectionSettingExpansion === 'sort'
                        ? 'show'
                        : collectionManager.version_collectionItemsSetting
                            .collection_item_no_display === 'collection_item_no_display_show'
                        ? 'show'
                        : 'hide'
                    }
                    showTitle={
                      $collectionSettingExpansion === 'sort'
                        ? 'title'
                        : collectionManager.version_collectionItemsSetting.collection_item_title ===
                          'collection_item_title_rtitle'
                        ? 'title'
                        : collectionManager.version_collectionItemsSetting.collection_item_title ===
                          'collection_item_title_sn'
                        ? 'serial'
                        : collectionManager.version_collectionItemsSetting.collection_item_title ===
                          'collection_item_title_custom'
                        ? 'custom'
                        : 'hide'
                    }
                    showIntroduction={
                      $collectionSettingExpansion === 'sort'
                        ? 'hide'
                        : collectionManager.version_collectionItemsSetting
                            .collection_item_descr_display === 'collection_item_descr_display_show'
                        ? 'show'
                        : 'hide'
                    }
                    resources={collectionManager.version_collectionItems}
                    onClick={
                      isRssCollection
                        ? undefined
                        : (itemID, resourceID) => {
                            set$authProcessorShow(get$authProcessorShow() + 1);
                            set$licensorId(resourceID);
                            set$itemId(itemID);
                          }
                    }
                    onChangeCustomTitle={
                      isRssCollection
                        ? undefined
                        : async (itemID, title) => {
                      await FServiceAPI.Resource.updateCollectionItemsInfo_Draft({
                        resourceId: collectionManager.version_resourceID,
                        data: [
                          {
                            itemId: itemID,
                            itemTitle: title,
                          },
                        ],
                      });
                      await onChange_needSaveDraft_collectionItems({});
                      await dispatch<ChangeAction>({
                        type: 'collectionManager/change',
                        payload: {
                          version_collectionItems: collectionManager.version_collectionItems.map(
                            (vc) => {
                              if (vc.itemID !== itemID) {
                                return vc;
                              }
                              return {
                                ...vc,
                                itemTitle: title,
                              };
                            },
                          ),
                        },
                      });
                    }}
                    onClickDelete={
                      isRssCollection
                        ? undefined
                        : async (itemID) => {
                      const { data } = await FServiceAPI.Resource.deleteCollectionItems_Draft({
                        resourceId: collectionManager.version_resourceID,
                        removeCollectionItemIds: [itemID],
                      });
                      await dispatch<ChangeAction>({
                        type: 'collectionManager/change',
                        payload: {
                          version_resources_pageCurrent: Math.min(
                            Math.floor(
                              (collectionManager.version_resources_totalCount - 1) /
                                collectionManager.version_resources_pageSize,
                            ) + 1,
                            collectionManager.version_resources_pageCurrent,
                          ),
                        },
                      });
                      await dispatch<Version_Fetch_CollectionItems_Action>({
                        type: 'collectionManager/version_Fetch_CollectionItems',
                      });
                      await onChange_needSaveDraft_collectionItems({});
                      if (collectionManager.version_resourceInfo) {
                        const {
                          data: data_collectionItems,
                        }: {
                          ret: number;
                          errCode: number;
                          data: {
                            skip: number;
                            limit: number;
                            totalItem: number;
                            dataList: [];
                          };
                        } = await FServiceAPI.Resource.getCollectionItems_Draft({
                          resourceId: collectionManager.version_resourceID,
                          limit: 1,
                        });

                        dispatch<ChangeAction>({
                          type: 'collectionManager/change',
                          payload: {
                            version_resourceInfo: {
                              ...collectionManager.version_resourceInfo,
                              itemsTotalCount: data_collectionItems.totalItem,
                            },
                          },
                        });
                      }
                    }}
                    onChangeIndex={
                      isRssCollection
                        ? undefined
                        : async (id, index, newList) => {
                      const { ret, errCode, msg, data } =
                        await FServiceAPI.Resource.setCollectionItemsSortID_Draft({
                          resourceId: collectionManager.version_resourceID,
                          data: {
                            itemIds: [id],
                            targetSortId:
                              collectionManager.version_collectionItemsSetting
                                ?.collection_sort_list === 'collection_sort_ascending'
                                ? (collectionManager.version_resources_pageCurrent - 1) *
                                    collectionManager.version_resources_pageSize +
                                  index +
                                  1
                                : collectionManager.version_resources_totalCount -
                                  (collectionManager.version_resources_pageCurrent - 1) *
                                    collectionManager.version_resources_pageSize -
                                  index,
                          },
                        });

                      if (ret !== 0 || errCode !== 0) {
                        fMessage(msg, 'error');
                        return;
                      }

                      await dispatch<Version_Fetch_CollectionItems_Action>({
                        type: 'collectionManager/version_Fetch_CollectionItems',
                      });
                      await onChange_needSaveDraft_collectionItems({});
                    }}
                  />
                )}

                {((collectionManager.version_collectionItemsSetting.collection_view ===
                  'collection_view_list' &&
                  collectionManager.version_resources_totalCount > 10) ||
                  (collectionManager.version_collectionItemsSetting.collection_view ===
                    'collection_view_card' &&
                    collectionManager.version_resources_totalCount > 6)) && (
                  <>
                    <div style={{ height: 20 }} />

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Pagination
                        showTitle={false}
                        locale={{
                          // items_per_page: '条/页',
                          items_per_page: FI18n.i18nNext.t('cqr_itemmgmt_list_pagenation_perpage'),
                          // jump_to: '前往',
                          jump_to: FI18n.i18nNext.t('cqr_itemmgmt_list_pagenation_currentpage'),
                          // page: '页',
                          page: ' ',
                        }}
                        size='small'
                        showSizeChanger
                        showQuickJumper
                        current={collectionManager.version_resources_pageCurrent}
                        pageSize={collectionManager.version_resources_pageSize}
                        total={collectionManager.version_resources_totalCount}
                        onChange={onChangePage}
                        showTotal={(total) => {
                          // return `共${total}条`;
                          return FI18n.i18nNext.tAuto('cqr_itemmgmt_list_pagenation_total', {
                            PageCount: total,
                          });
                        }}
                        pageSizeOptions={
                          collectionManager.version_collectionItemsSetting.collection_view ===
                          'collection_view_card'
                            ? [6, 12, 18, 24, 30]
                            : [10, 20, 30, 40, 50]
                        }
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {collectionManager.version_resources_state === 'noData' && (
              <>
                <div style={{ height: 10 }} />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  {/*<span>{FI18n.i18nNext.tAuto('resourceoptions_list_empty')}</span>*/}
                  <FComponentsLib.FContentText
                    text={FI18n.i18nNext.t('cqr_itemmgmt_empty_msg')}
                    type={'additional2'}
                  />
                  <div style={{ height: 20 }} />
                  <FComponentsLib.FRectBtn
                    disabled={isRssCollection}
                    onClick={async () => {
                      const result: string[] | null = await fSelectResourcesAsCollectionItems({
                        collectionID: collectionManager.version_resourceID,
                      });
                      if (!result || result.length === 0) {
                        return;
                      }

                      set$resourceIDs(result);
                    }}
                  >
                    {/*添加单品资源*/}
                    {FI18n.i18nNext.tAuto('cqr_itemmgmt_empty_btn_additem')}
                  </FComponentsLib.FRectBtn>
                </div>
                <div style={{ height: 20 }} />
              </>
            )}
          </div>
          )}

          {!showFullImportPanel && (
          <>
          <div style={{ height: 5 }} />

          <div className={styles.block}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <FComponentsLib.FContentText
                text={FI18n.i18nNext.t('resourceinfo_title')}
                type={'highlight'}
              />
              {collectionManager.version_customProperties.length < 30 && (
                <FTooltip title={FI18n.i18nNext.tAuto('resourceinfo_add_btn_info')}>
                  <div>
                    <FComponentsLib.FTextBtn
                      style={{
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                      type='primary'
                      disabled={isRssCollection}
                      onClick={async () => {
                        const dataSource: {
                          key: string;
                          name: string;
                          // value: string;
                          description: string;
                        } | null = await fResourcePropertyEditor3({
                          disabledKeys: [
                            ...collectionManager.version_systemProperties.map<string>(
                              (rp) => rp.key,
                            ),
                            ...collectionManager.version_customProperties.map<string>(
                              (bp) => bp.key,
                            ),
                            ...collectionManager.version_customConfigurations.map<string>(
                              (pp) => pp.key,
                            ),
                          ],
                          disabledNames: [
                            ...collectionManager.version_systemProperties.map<string>(
                              (sp) => sp.name,
                            ),
                            ...collectionManager.version_customProperties.map<string>(
                              (bp) => bp.name,
                            ),
                            ...collectionManager.version_customConfigurations.map<string>(
                              (pp) => pp.name,
                            ),
                          ],
                        });
                        // console.log(dataSource, 'dataSource9iojskldjflksdjflk');
                        if (!dataSource) {
                          return;
                        }

                        const finalDate: CollectionManagerState['version_customProperties'] = [
                          ...collectionManager.version_customProperties,
                          {
                            ...dataSource,
                            value: '',
                          },
                        ];

                        await onChange_needSaveDraft({
                          version_customProperties: finalDate,
                        });
                      }}
                    >
                      <FIcons.FProperty style={{ fontSize: 14 }} />
                      <span>{FI18n.i18nNext.tAuto('resourceinfo_add_btn')}</span>
                    </FComponentsLib.FTextBtn>
                  </div>
                </FTooltip>
              )}
            </div>

            {collectionManager.version_systemProperties.length === 0 &&
              collectionManager.version_customProperties.length === 0 && (
                <>
                  <div style={{ height: 10 }} />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FComponentsLib.FContentText
                      text={FI18n.i18nNext.t('resourceinfo_empty_msg')}
                      type={'additional2'}
                      style={{ maxWidth: 600, textAlign: 'center' }}
                    />
                  </div>
                  <div style={{ height: 20 }} />
                </>
              )}

            {!(
              collectionManager.version_systemProperties.length === 0 &&
              collectionManager.version_customProperties.length === 0
            ) && (
              <>
                <div style={{ height: 20 }} />
                <FAttrsAndConfigs
                  dateSource={[
                    ...collectionManager.version_systemProperties.map<IAttrsAndConfigsItem>(
                      (sp) => {
                        return {
                          key: sp.key,
                          value: sp.value,
                          name: sp.name,
                          description: sp.description,
                          configs: {
                            item_Deletable: false,
                            key_Editable: false,
                            name_Editable: false,
                            description_Editable: false,
                            value_Editable: isRssCollection
                              ? false
                              : (sp.type === 'additional' && sp.valueConfig) || false,
                          },
                        };
                      },
                    ),
                    ...collectionManager.version_customProperties.map<IAttrsAndConfigsItem>(
                      (cp) => {
                        return {
                          key: cp.key,
                          value: cp.value,
                          name: cp.name,
                          description: cp.description,
                          configs: {
                            item_Deletable: !isRssCollection,
                            key_Editable: !isRssCollection,
                            name_Editable: !isRssCollection,
                            description_Editable: !isRssCollection,
                            value_Editable: isRssCollection
                              ? false
                              : {
                                  text: {
                                    nullable: true,
                                    minLength: 0,
                                    maxLength: 140,
                                  },
                                },
                          },
                        };
                      },
                    ),
                  ]}
                  onChangeItem={
                    isRssCollection
                      ? undefined
                      : async (oldData, newData, index, changedFields) => {
                    if (index < collectionManager.version_systemProperties.length) {
                      const newDateSource = [...collectionManager.version_systemProperties];
                      newDateSource[index] = {
                        key: newData.key,
                        name: newData.name,
                        value: newData.value,
                        description: newData.description,
                        type: collectionManager.version_systemProperties[index].type,
                        valueConfig: collectionManager.version_systemProperties[index].valueConfig,
                      };

                      await onChange_needSaveDraft({
                        version_systemProperties: newDateSource,
                      });
                    } else {
                      const newDateSource = [...collectionManager.version_customProperties];
                      newDateSource[index - collectionManager.version_systemProperties.length] = {
                        key: newData.key,
                        name: newData.name,
                        value: newData.value,
                        description: newData.description,
                      };
                      await onChange_needSaveDraft({
                        version_customProperties: newDateSource,
                      });
                    }
                  }}
                  onDeleteItem={
                    isRssCollection
                      ? undefined
                      : async (value) => {
                    const finalDate: CollectionManagerState['version_customProperties'] =
                      collectionManager.version_customProperties.filter((v, i) => {
                        return v.key !== value.key && v.name !== value.name;
                      });

                    await onChange_needSaveDraft({
                      version_customProperties: finalDate,
                    });
                  }}
                  disabledKeys={[
                    ...collectionManager.version_systemProperties.map<string>((rp) => rp.key),
                    ...collectionManager.version_customProperties.map<string>((cp) => cp.key),
                    ...collectionManager.version_customConfigurations.map<string>((cp) => cp.key),
                  ]}
                  disabledNames={[
                    ...collectionManager.version_systemProperties.map<string>((rp) => rp.name),
                    ...collectionManager.version_customProperties.map<string>((cp) => cp.name),
                    ...collectionManager.version_customConfigurations.map<string>((cp) => cp.name),
                  ]}
                />
              </>
            )}
          </div>

          {collectionManager.version_resourceTypeConfig.isSupportOptionalConfig && (
            <>
              <div style={{ height: 5 }} />
              <div className={styles.block}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <FComponentsLib.FContentText
                    text={FI18n.i18nNext.t('resourceoptions_title')}
                    type={'highlight'}
                  />
                  {collectionManager.version_customConfigurations.length < 30 && (
                    <FTooltip title={FI18n.i18nNext.tAuto('info_versionoptions')}>
                      <div>
                        <FComponentsLib.FTextBtn
                          style={{
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                          }}
                          type='primary'
                          disabled={isRssCollection}
                          onClick={async () => {
                            const dataSource: {
                              key: string;
                              name: string;
                              type: 'input' | 'select';
                              input: string;
                              select: string[];
                              description: string;
                            } | null = await fResourceOptionEditor({
                              disabledKeys: [
                                ...collectionManager.version_systemProperties.map<string>(
                                  (sp) => sp.key,
                                ),
                                ...collectionManager.version_customProperties.map<string>(
                                  (bp) => bp.key,
                                ),
                                ...collectionManager.version_customConfigurations.map<string>(
                                  (pp) => pp.key,
                                ),
                              ],
                              disabledNames: [
                                ...collectionManager.version_systemProperties.map<string>(
                                  (sp) => sp.name,
                                ),
                                ...collectionManager.version_customProperties.map<string>(
                                  (bp) => bp.name,
                                ),
                                ...collectionManager.version_customConfigurations.map<string>(
                                  (pp) => pp.name,
                                ),
                              ],
                            });

                            if (!dataSource) {
                              return;
                            }

                            const finalData: CollectionManagerState['version_customConfigurations'] =
                              [...collectionManager.version_customConfigurations, dataSource];

                            await onChange_needSaveDraft({
                              version_customConfigurations: finalData,
                            });
                          }}
                        >
                          <FIcons.FConfiguration style={{ fontSize: 14 }} />
                          <span>{FI18n.i18nNext.tAuto('resourceoptions_add_btn')}</span>
                        </FComponentsLib.FTextBtn>
                      </div>
                    </FTooltip>
                  )}
                </div>

                {collectionManager.version_customConfigurations.length === 0 && (
                  <>
                    <div style={{ height: 10 }} />
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {/*<span>{FI18n.i18nNext.tAuto('resourceoptions_list_empty')}</span>*/}
                      <FComponentsLib.FContentText
                        text={FI18n.i18nNext.t('resourceoptions_list_empty')}
                        type={'additional2'}
                      />
                    </div>
                    <div style={{ height: 20 }} />
                  </>
                )}

                {collectionManager.version_customConfigurations.length > 0 && (
                  <>
                    <div style={{ height: 20 }} />
                    <FResourceOptions
                      theme={'dark'}
                      dataSource={collectionManager.version_customConfigurations}
                      onEdit={
                        isRssCollection
                          ? undefined
                          : async (value) => {
                        const index: number =
                          collectionManager.version_customConfigurations.findIndex((p) => {
                            return p === value;
                          });

                        const dataSource: {
                          key: string;
                          name: string;
                          type: 'input' | 'select';
                          input: string;
                          select: string[];
                          description: string;
                        } | null = await fResourceOptionEditor({
                          disabledKeys: [
                            ...collectionManager.version_systemProperties.map<string>(
                              (sp) => sp.key,
                            ),
                            ...collectionManager.version_customProperties.map<string>(
                              (bp) => bp.key,
                            ),
                            ...collectionManager.version_customConfigurations.map<string>(
                              (pp) => pp.key,
                            ),
                          ],
                          disabledNames: [
                            ...collectionManager.version_systemProperties.map<string>(
                              (sp) => sp.name,
                            ),
                            ...collectionManager.version_customProperties.map<string>(
                              (bp) => bp.name,
                            ),
                            ...collectionManager.version_customConfigurations.map<string>(
                              (pp) => pp.name,
                            ),
                          ],
                          defaultData: value,
                        });

                        if (!dataSource) {
                          return;
                        }

                        const finalData: CollectionCreatorPageModelState['step2_customConfigurations'] =
                          collectionManager.version_customConfigurations.map((a, b) => {
                            if (b !== index) {
                              return a;
                            }
                            return dataSource;
                          });

                        await onChange_needSaveDraft({
                          version_customConfigurations: finalData,
                        });
                      }}
                      onDelete={
                        isRssCollection
                          ? undefined
                          : async (value) => {
                        const finalData: CollectionCreatorPageModelState['step2_customConfigurations'] =
                          collectionManager.version_customConfigurations.filter((a) => {
                            return a.key !== value.key && a.name !== value.name;
                          });
                        await onChange_needSaveDraft({
                          version_customConfigurations: finalData,
                        });
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}

          {!isRssCollection && collectionManager.version_reload > 0 && (
            <>
              <div style={{ height: 5 }} />
              <div className={styles.block}>
                {collectionManager.version_reload > 0 && (
                  <FMicroAPP_Authorization
                    licenseeId={collectionManager.version_resourceID}
                    mainAppType={'resourceInVersionUpdate'}
                    depList={collectionManager.version_directDependencies}
                    upcastList={collectionManager.version_baseUpcastResources}
                    mount={(data) => {
                      dispatch<ChangeAction>({
                        type: 'collectionManager/change',
                        payload: {
                          version_directDependencies: data.directDependencies || [],
                          version_baseUpcastResources: data.baseUpcastResources || [],
                          version_authExcludedItems: data.authExcludedItems || [],
                        },
                      });
                    }}
                    update={async (data) => {
                      await onChange_needSaveDraft({
                        version_directDependencies: data.directDependencies || [],
                        // version_resolveResources: data.resolveResources,
                        version_baseUpcastResources: data.baseUpcastResources || [],
                        version_authExcludedItems: data.authExcludedItems || [],
                      });
                    }}
                  />
                )}
              </div>
            </>
          )}

          <div style={{ height: 5 }} />

          <VersionDescriptionEditor
            value={collectionManager.version_descriptionText}
            actionsDisabled={isRssCollection}
            onChange={async (value) => {
              await onChange_needSaveDraft({
                version_descriptionText: value,
              });
            }}
          />

          {collectionManager.version_graphShow && (
            <>
              <div style={{ height: 5 }} />
              <div className={styles.block}>
                <FComponentsLib.FContentText text={'相关视图'} type={'highlight'} />
                <div style={{ height: 20 }} />

                <FViewportCards_Resource
                  resourceID={collectionManager.version_resourceID}
                  version={collectionManager.version_version}
                  graphShow={['authorization', 'dependency']}
                  onMount={({ hasData }) => {
                    // console.log(hasData, 'hasDataiosdjflkjsdflkjlk');
                    dispatch<ChangeAction>({
                      type: 'collectionManager/change',
                      payload: {
                        version_graphShow: hasData,
                      },
                    });
                  }}
                />
              </div>
            </>
          )}
          </>
          )}
        </div>
        <div style={{ height: 100 }} />
        </div>
      </div>

      {collectionManager.version_resourceID !== '' && $resourceIDs.length === 0 && (
        <FContractHandleDrawer
          open={$authProcessorShow}
          licensorId={$licensorId}
          itemId={$itemId}
          licenseeId={collectionManager.version_resourceID}
          onUpdate={async (options) => {
            await dispatch<Version_Fetch_CollectionItems_Action>({
              type: 'collectionManager/version_Fetch_CollectionItems',
            });
            await onChange_needSaveDraft_collectionItems({});
          }}
          onClose={() => {
            set$licensorId('');
            set$itemId('');
            set$authProcessorShow(0);
          }}
        />
      )}

      {rssSource === 'no' &&
        (collectionManager.version_collectionItemsChanged ||
          collectionManager.version_otherChanged) && (
        <div className={styles.bottomDraft}>
          <div className={styles.bottomDraftContent}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <FIcons.FInfo style={{ fontSize: 12, color: '#999', lineHeight: '18px' }} />
              <FComponentsLib.FContentText
                type={'additional2'}
                text={FI18n.i18nNext.t('edit_collection_itemmgnt_submit_info')}
                style={{ color: '#C4C4C4' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 30 }}>
              {collectionManager.version_draftSaveTime !== '' &&
                !collectionManager.version_draftSaving && (
                  <FComponentsLib.FContentText
                    // text={`已保存 ${collectionManager.version_draftSaveTime}`}
                    text={FI18n.i18nNext.t('edit_collection_itemmgnt_savedraft_state_saved', {
                      LastEditTime: collectionManager.version_draftSaveTime,
                    })}
                    type={'additional2'}
                  />
                )}

              {collectionManager.version_draftSaving && (
                <FComponentsLib.FContentText
                  text={FI18n.i18nNext.t('edit_collection_itemmgnt_savedraft_state_saving')}
                  type={'additional2'}
                />
              )}

              {/*{FI18n.i18nNext.tAuto('rqr_step3_btn_later')}*/}
              <FComponentsLib.FTextBtn
                type={'default'}
                disabled={isRssCollection}
                onClick={() => {
                  dispatch<Version_SaveDraft_Action>({
                    type: 'collectionManager/version_SaveDraft',
                  });
                }}
              >
                {FI18n.i18nNext.tAuto('save_as_draft')}
              </FComponentsLib.FTextBtn>
              <FComponentsLib.FRectBtn
                disabled={
                  isRssCollection ||
                  !(
                    collectionManager.version_collectionItemsChanged ||
                    collectionManager.version_otherChanged
                  )
                }
                type={'primary'}
                onClick={async () => {
                  const result = await dispatch<Version_SaveDate_Action>({
                    type: 'collectionManager/version_SaveDate',
                  });
                  if (!result) {
                    return;
                  }

                  await dispatch<ChangeAction>({
                    type: 'collectionManager/change',
                    payload: {
                      version_collectionItemsChanged: false,
                      version_otherChanged: false,
                    },
                  });

                  run_saveDraft();
                }}
              >
                {FI18n.i18nNext.tAuto('edit_collection_itemmgnt_submit_btn')}
              </FComponentsLib.FRectBtn>
            </div>
          </div>
        </div>
      )}

      <ChangeLogDrawer
        collectionID={collectionManager.version_resourceID}
        open={$logDrawerOpen}
        onClose={() => {
          set$logDrawerOpen(false);
        }}
      />

      {collectionManager.version_resourceID !== '' && $resourceIDs.length > 0 && (
        <FAddResourcesHandleAuth
          resourceIDs={$resourceIDs}
          collectionID={collectionManager.version_resourceID}
          isPublish={false}
          onCancel={() => {
            set$resourceIDs([]);
          }}
          onOk={async () => {
            set$resourceIDs([]);

            await onChange_needSaveDraft_collectionItems({});

            await dispatch<Version_Fetch_CollectionItems_Action>({
              type: 'collectionManager/version_Fetch_CollectionItems',
            });

            if (collectionManager.version_resourceInfo) {
              const {
                data: data_collectionItems,
              }: {
                ret: number;
                errCode: number;
                data: {
                  skip: number;
                  limit: number;
                  totalItem: number;
                  dataList: [];
                };
              } = await FServiceAPI.Resource.getCollectionItems_Draft({
                resourceId: collectionManager.version_resourceID,
                limit: 1,
              });

              dispatch<ChangeAction>({
                type: 'collectionManager/change',
                payload: {
                  version_resourceInfo: {
                    ...collectionManager.version_resourceInfo,
                    itemsTotalCount: data_collectionItems.totalItem,
                  },
                },
              });
            }
          }}
          onBack={async () => {
            set$resourceIDs([]);

            const result: string[] | null = await fSelectResourcesAsCollectionItems({
              collectionID: collectionManager.version_resourceID,
            });
            if (!result || result.length === 0) {
              return;
            }

            set$resourceIDs(result);
          }}
          onError={() => {
            set$resourceIDs([]);
          }}
        />
      )}

      <SortOrderModal
        setting={collectionManager.version_collectionItemsSetting}
        open={$sortOrderModalShow}
        collectionItems={collectionManager.version_collectionItems}
        onChangeSetting={async (value) => {
          await onChange_needSaveDraft({
            version_collectionItemsSetting: value,
          });
        }}
        onChangeSortSetting={async (sort) => {
          await dispatch<Version_Fetch_CollectionItems_Action>({
            type: 'collectionManager/version_Fetch_CollectionItems',
          });
        }}
        onClickSortBy={async (sort: 'added' | 'title' | 'updateDate') => {
          const { ret, errCode, msg, data } =
            await FServiceAPI.Resource.reorderCollectionItems_Draft({
              resourceId: collectionManager.version_resourceID,
              sortField:
                sort === 'added'
                  ? 'createDate'
                  : sort === 'title'
                  ? 'itemTitle'
                  : 'resourceUpdateDate',
              sortType: sort === 'added' ? -1 : sort === 'title' ? 1 : -1,
            });

          if (ret !== 0 || errCode !== 0) {
            fMessage(msg, 'error');
            return;
          }
          await dispatch<Version_Fetch_CollectionItems_Action>({
            type: 'collectionManager/version_Fetch_CollectionItems',
          });
        }}
        onChangeItemsSortID={async (itemIDs: string[], sortID: number) => {
          const { ret, errCode, msg, data } =
            await FServiceAPI.Resource.setCollectionItemsSortID_Draft({
              resourceId: collectionManager.version_resourceID,
              data: {
                itemIds: itemIDs,
                targetSortId: sortID,
              },
            });

          if (ret !== 0 || errCode !== 0) {
            fMessage(msg, 'error');
            return;
          }
          // set$checkedItemIDs([]);

          await dispatch<Version_Fetch_CollectionItems_Action>({
            type: 'collectionManager/version_Fetch_CollectionItems',
          });
          await onChange_needSaveDraft_collectionItems({});
        }}
        pageSize={collectionManager.version_resources_pageSize}
        pageCurrent={collectionManager.version_resources_pageCurrent}
        totalCount={collectionManager.version_resources_totalCount}
        onChangePage={onChangePage}
        onCancel={() => {
          set$sortOrderModalShow(false);
        }}
      />
    </>
  );
}

export default connect(({ collectionManager }: ConnectState) => ({
  collectionManager: collectionManager,
}))(VersionInfo);

interface VersionDescriptionEditorBlock {
  value: string;
  actionsDisabled?: boolean;

  onChange?(value: string): void;
}

export function VersionDescriptionEditor({
  value,
  actionsDisabled = false,
  onChange,
}: VersionDescriptionEditorBlock) {
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>(value);
  const [$isEditing, set$isEditing] = FUtil.Hook.useGetState<boolean>(false);
  const [$editor, set$editor] = FUtil.Hook.useGetState<EditorState>(
    BraftEditor.createEditorState(value),
  );

  AHooks.useDebounceEffect(
    () => {
      if (get$value() !== value) {
        set$value(value);
        set$editor(BraftEditor.createEditorState(value));
      }
    },
    [value],
    {
      wait: 100,
    },
  );

  return (
    <div className={styles.block}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <FComponentsLib.FContentText
          text={FI18n.i18nNext.t('edit_collection_itemmgnt_descr')}
          type={'highlight'}
        />

        <Space size={10}>
          {$isEditing && (
            <>
              {/*<FComponentsLib.FTextBtn*/}
              {/*  type='default'*/}
              {/*  onClick={() => {*/}
              {/*    onChange({ descriptionFullScreen: true });*/}
              {/*  }}*/}
              {/*>全屏编辑</FComponentsLib.FTextBtn>*/}
              {/*<FDivider />*/}
              <FComponentsLib.FTextBtn
                type='default'
                disabled={actionsDisabled}
                onClick={() => {
                  set$isEditing(false);
                }}
              >
                {FI18n.i18nNext.tAuto('cancel')}
              </FComponentsLib.FTextBtn>
              <FComponentsLib.FTextBtn
                type='primary'
                disabled={actionsDisabled}
                onClick={async () => {
                  const html: string = $editor.toHTML();
                  const description: string = html === '<p></p>' ? '' : html;
                  set$value(description);
                  onChange && onChange(description);
                  set$isEditing(false);
                }}
              >
                {FI18n.i18nNext.tAuto('save')}
              </FComponentsLib.FTextBtn>
            </>
          )}
          {!$isEditing && value !== '' && (
            <>
              <FComponentsLib.FTextBtn
                type='primary'
                disabled={actionsDisabled}
                onClick={() => {
                  set$isEditing(true);
                }}
              >
                编辑
              </FComponentsLib.FTextBtn>
            </>
          )}
        </Space>
      </div>

      {value === '' && !$isEditing && (
        <>
          <div style={{ height: 10 }} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <FComponentsLib.FContentText
              // text={'动动手，让你的资源看起来更丰富～'}
              // text={FI18n.i18nNext.tAuto('edit_collection_itemmgnt_descr_empty_info')}
              text={FI18n.i18nNext.t('version_description_empty_msg')}
              type={'additional2'}
            />
            <div style={{ height: 20 }} />

            <FComponentsLib.FRectBtn
              type={'primary'}
              disabled={actionsDisabled}
              onClick={() => {
                set$isEditing(true);
              }}
            >
              {/*添加版本描述*/}
              {/*{FI18n.i18nNext.tAuto('edit_collection_itemmgnt_descr_empty_btn')}*/}
              {FI18n.i18nNext.tAuto('version_description_empty_btn')}
            </FComponentsLib.FRectBtn>
          </div>
        </>
      )}

      {$isEditing && (
        <>
          <div style={{ height: 20 }} />

          <FBraftEditor
            value={$editor}
            // defaultValue={editorText}
            onChange={(value: EditorState) => {
              set$editor(value);
            }}
            style={{ minHeight: 500 }}
          />
        </>
      )}

      {!$isEditing && value && (
        <>
          <div style={{ height: 20 }} />
          <div className={styles.description}>
            <FHtmlReactParser str={value} />
          </div>
        </>
      )}
    </div>
  );
}
